import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useDispatch } from 'react-redux';
import { logout } from '@/store/slices/authSlice';
import Loader from '@/components/Loader';

import {
  Home,
  Users,
  Settings,
  BarChart3,
  FileText,
  UserCog,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Bell,
  CreditCard,
  Shield,
  Database,
  Activity,
  Calendar,
  Mail,
  Globe,
  Zap,
  HelpCircle,
  Search,
  Upload,
  Download,
  Archive,
  Monitor,
  Smartphone,
  Key,
  LogOut,
  MessageSquare,
  Video,
  Volume2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/utils';
import { useCurrentUser } from '@/hooks/redux';
import { UserRole } from '@/types';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

interface NavItem {
  name: string;
  href?: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
  badge?: string;
  children?: NavItem[];
}

const navigation: NavItem[] = [
  // { name: 'Dashboard', href: '/dashboard', icon: Home },
  { name: 'Users', icon: Users, href: '/users' },
  { name: 'Transit Tables', icon: FileText, href: '/transit-tables' },
  { name: 'Predictions Prompt Data', icon: Shield, href: '/predictions-prompt-data' },
  { name: 'Sync Prediction', icon: Zap, href: '/sync-prediction' },
  { name: 'Generate Audio', icon: Volume2, href: '/generate-audio' },
  { name: 'S3 Video Upload', icon: Video, href: '/s3-video-uploads' },
  { name: 'S3 Videos', icon: Video, href: '/s3-videos' },
  { name: 'User Feedbacks', icon: MessageSquare, href: '/feedbacks' },
  { name: 'Waiting List', icon: Mail, href: '/waiting-list' },
  { name: 'Profile', href: '/profile', icon: UserCog },
];

interface NavItemComponentProps {
  item: NavItem;
  isOpen: boolean;
  level?: number;
  onNavigateStart: () => void;
}

function NavItemComponent({ item, isOpen, level = 0, onNavigateStart }: NavItemComponentProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const location = useLocation();
  const user = useCurrentUser();
  const isAdmin = user?.role === UserRole.ADMIN;

  if (item.adminOnly && !isAdmin) return null;

  const hasChildren = item.children && item.children.length > 0;
  const isActive = item.href ? location.pathname === item.href : false;
  const hasActiveChild = item.children?.some(
    (child) => child.href && location.pathname.startsWith(child.href)
  );

  React.useEffect(() => {
    if (hasActiveChild && isOpen) {
      setIsExpanded(true);
    }
  }, [hasActiveChild, isOpen]);

  const handleClick = () => {
    if (hasChildren) {
      setIsExpanded(!isExpanded);
    }
  };

  const Icon = item.icon;
  const indent = level * 16;

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={handleClick}
          className={cn(
            'flex items-center justify-center rounded-lg py-2 transition-colors',
            isActive || hasActiveChild ? 'bg-primary text-white' : 'text-white hover:bg-accent',

            isOpen ? 'w-full px-3' : 'w-10 h-10'
          )}
          style={{ paddingLeft: isOpen ? `${12 + indent}px` : undefined }}
        >
          <Icon className={cn('h-5 w-5', isOpen && 'mr-3')} />
          {isOpen && (
            <>
              <span className="flex-1 text-left">{item.name}</span>
              {isExpanded ? (
                <ChevronUp className="ml-2 h-4 w-4" />
              ) : (
                <ChevronDown className="ml-2 h-4 w-4" />
              )}
            </>
          )}
        </button>

        {hasChildren && isExpanded && isOpen && (
          <div className="mt-1 space-y-1">
            {item.children!.map((child) => (
              <NavItemComponent
                key={child.name}
                item={child}
                isOpen={isOpen}
                level={level + 1}
                onNavigateStart={onNavigateStart}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.href!}
      onClick={onNavigateStart}
      className={({ isActive }) =>
        cn(
          'flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          isActive ? 'bg-primary text-white' : 'text-white',

          !isOpen && 'justify-center'
        )
      }
      style={{ paddingLeft: isOpen ? `${12 + indent}px` : undefined }}
    >
      <Icon className={cn('h-5 w-5', isOpen && 'mr-3')} />
      {isOpen && <span>{item.name}</span>}
    </NavLink>
  );
}

export function EnhancedSidebar({ isOpen, onToggle }: SidebarProps) {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showLoader, setShowLoader] = useState(false);

  const handleNavigateStart = () => {
    setShowLoader(true);
    setTimeout(() => setShowLoader(false), 300);
  };

  const handleLogout = () => {
    setShowLoader(true);
    setTimeout(() => {
      dispatch(logout());
      navigate('/login', { replace: true });
      setShowLoader(false);
    }, 300);
  };

  return (
    <>
      {/* {showLoader && <Loader />} */}
      {isOpen && <div className="fixed inset-0 z-20 bg-black/50 lg:hidden" onClick={onToggle} />}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-30 flex flex-col bg-sidebar-gradient border-r transition-all duration-300 overflow-hidden',
          isOpen ? 'w-80 translate-x-0' : 'w-16 -translate-x-full lg:translate-x-0'
        )}
      >
        <div className="flex h-16 items-center border-b px-4">
          <div className={cn('flex items-center', isOpen ? 'w-full' : 'justify-center')}>
            <div
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-lg bg-white transition-opacity',
                isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
              )}
            >
              <img src="/login/icon.png" alt="App Logo" className="h-5 w-5" />
            </div>

            {isOpen && (
              <div className="ml-3 flex-1">
                <span className="text-lg font-semibold text-white">Admin</span>
                <div className="text-xs text-muted-foreground text-white">MeraAstro</div>
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className={cn(
              'hidden lg:flex h-7 w-7 bg-white text-black hover:bg-primary transition-colors',
              isOpen ? 'ml-auto' : 'absolute left-1/2 -translate-x-1/2 top-3 z-40'
            )}
          >
            {isOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </div>

        <nav className="flex-1 space-y-1 p-2 overflow-y-auto">
          {navigation.map((item) => (
            <NavItemComponent
              key={item.name}
              item={item}
              isOpen={isOpen}
              onNavigateStart={handleNavigateStart}
            />
          ))}
        </nav>

        <div className="border-t p-3">
          <Button
            className={cn(
              `group whitespace-nowrap text-sm disabled:pointer-events-none py-2 w-full h-full font-semibold font-spectral text-black uppercase bg-white inner-shadow-blue rounded-lg cursor-pointer transition-all duration-500 ease-in-out hover:bg-[linear-gradient(to_right,#082261,#34065c)] hover:text-white border border-transparent hover:border-white`,
              !isOpen && 'px-0'
            )}
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-2 text-current" />
            {isOpen && 'Logout'}
          </Button>
        </div>
      </div>
    </>
  );
}
